import I18n from 'i18n-js';
import React, { useContext, useState } from 'react';

import { core_apimessages_ShippingLabelPackage } from '@reverbdotcom/commons/src/gql/graphql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { gql } from '@reverbdotcom/commons/src/gql';
import { RCButton, RCTextWithIcon, RCLoadingBars } from '@reverbdotcom/cadence/components';
import { PencilSimpleIcon, PlusIcon } from '@reverbdotcom/cadence/icons/react';

import { SellFormContext } from '../../SellFormContext';
import { buildPackageEntry, packageEntryComplete, packageSummary } from '../../../../discovery/shipping_labels/shipping_label_package';
import { carrierCalculatedPackageValidator } from './carrierCalculatedPackageValidator';
import { SelectedCarrier } from './SelectedCarrier';
import CarrierCalcAddOns from './CarrierCalcAddOns';
import CarrierCalculatedForm from './CarrierCalculatedForm';
import { CarrierHighlights } from './CarrierHighlights';
import { Field, ActionType } from '../../sellFormReducerTypes';
import CarrierCalculatedDetailsBox from './CarrierCalculatedDetailsBox';

export default function CarrierCalculatedFormContainer() {
  const { currentCanonicalProductId, dispatch, listingEntry, listingState, showRequiredFieldError } = useContext(SellFormContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const packageEntry = listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const currentPackageEntryCameFromReverbSuggestion = !!packageEntry.packageSizeSuggestionId;
  const packageValidator = carrierCalculatedPackageValidator(packageEntry);
  const highlightError = showRequiredFieldError && (packageValidator?.errorTranslationKey || !packageEntryComplete(packageEntry));

  function setPackageEntry(updatedPackage: core_apimessages_ShippingLabelPackage) {
    const upcomingPackageValidator = carrierCalculatedPackageValidator(updatedPackage);
    const hasValidSelectedCarrier = !upcomingPackageValidator?.errorTranslationKey && upcomingPackageValidator?.carrier;
    const allowedCarriers = hasValidSelectedCarrier ? [upcomingPackageValidator?.carrier] : null;

    dispatch({
      type: ActionType.UPDATE_FIELD,
      fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
      value: { ...updatedPackage, ...{ allowedCarriers } },
    });
  }

  function onPackageSuggestionResponse(respData) {
    const suggestionData = respData?.canonicalproduct?.packageSizeSuggestion;
    const validSuggestionData = !!suggestionData && !carrierCalculatedPackageValidator(suggestionData)?.errorTranslationKey;
    const currentPackageEntryIsBlank = ['length', 'width', 'height', 'weight'].every((dimensionKey) => !packageEntry[dimensionKey].value);
    const suggestionDiffersFromCurrentEntry = validSuggestionData && ['length', 'width', 'height', 'weight'].some((dimensionKey) => {
      return Number(suggestionData[dimensionKey]?.value) !== Number(packageEntry[dimensionKey]?.value) ||
        suggestionData[dimensionKey]?.unit !== packageEntry[dimensionKey]?.unit;
    });

    if (!validSuggestionData) { return; }

    const packageEntryFromSuggestionData = buildPackageEntry(
      {
        ...suggestionData,
        forceSafeShipping: true,
        forceSignatureRequired: true,
      },
      undefined,
      suggestionData.id,
    );

    const shouldActivateSuggestion = (currentPackageEntryIsBlank || currentPackageEntryCameFromReverbSuggestion) && suggestionDiffersFromCurrentEntry;

    if (shouldActivateSuggestion) {
      setPackageEntry(packageEntryFromSuggestionData);
    }
  }

  const { loading } = useQuery(
    FETCH_PACKAGE_SIZE_SUGGESTION,
    {
      skip: !currentCanonicalProductId || listingState !== 'draft',
      variables: { canonicalProductId: currentCanonicalProductId },
      onCompleted: onPackageSuggestionResponse,
    },
  );

  if (loading) {
    return (
      <div className="d-flex fx-align-center fx-justify-center mtb-2 mobile-mt-4">
        <RCLoadingBars />
      </div>
    );
  }

  return (
    <>
      <div
        className="mt-2"
        onClick={() => setIsModalOpen(true)}
        onKeyPress={() => setIsModalOpen(true)}
        role="button"
        tabIndex={0}
      >
        <CarrierCalculatedDetailsBox
          errorText={highlightError && I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.pleaseAdd')}
          footerContent={(
            <>
              {!!packageEntry.allowedCarriers?.length && (<SelectedCarrier carrier={packageEntry.allowedCarriers[0]} />)}

              {!packageEntry.allowedCarriers?.length && (<CarrierHighlights />)}
            </>
          )}
        >
          <div className="d-flex fx-align-center fx-justify-between padding-5">
            <div>
              <p className="mb-0 text-default-400 text-default--bold">
                {packageEntryComplete(packageEntry) && packageSummary(packageEntry)}

                {!packageEntryComplete(packageEntry) && I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.pleaseAdd')}
              </p>

              {currentPackageEntryCameFromReverbSuggestion && (
                <p className="mb-0 color-secondary text-default-350">
                  {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.packageSuggestionHint')}
                </p>
              )}
            </div>

            <RCButton size="mini" onClick={() => setIsModalOpen(true)}>
              <RCTextWithIcon
                svgComponent={packageEntryComplete(packageEntry) ? PencilSimpleIcon : PlusIcon}
                placement="left"
              >
                {packageEntryComplete(packageEntry) ?
                  I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.editButton') :
                  I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.addButton')
                }
              </RCTextWithIcon>
            </RCButton>
          </div>
        </CarrierCalculatedDetailsBox>
      </div>

      {isModalOpen && (
        <CarrierCalculatedForm
          packageEntry={packageEntry}
          setPackageEntry={setPackageEntry}
          setIsModalOpen={setIsModalOpen}
        />
      )}

      <CarrierCalcAddOns />
    </>
  );
}

export const FETCH_PACKAGE_SIZE_SUGGESTION = gql(`
  query Core_SellForm_FetchPackageSizeSuggestion($canonicalProductId: String!) {
    canonicalproduct(input: { id: $canonicalProductId }) {
      _id
      packageSizeSuggestion {
        _id
        id
        length {
          unit
          value
        }
        width {
          unit
          value
        }
        height {
          unit
          value
        }
        weight {
          unit
          value
        }
      }
    }
  },
`);
